import { useState, useEffect } from "react";
import {
  Row,
  Col,
  Select,
  Button,
  DatePicker,
  Typography,
  Space,
  Tag,
  Checkbox,
  Collapse,
  message,
} from "antd";
import { CloudDownloadOutlined, SwapOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  user_managementApi,
  get_user_ga4_properties,
  build_analytics_summary_test_content,
  generate_web_analytics_summary,
} from "../../api/admin.apis";

const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

/** Flatten GA4 properties from the admin properties endpoint response. */
function flattenGa4Properties(data) {
  if (!data) return [];

  if (Array.isArray(data)) {
    return data.map((p) => ({
      id: p.id || p.property_id,
      name: p.name || p.display_name || String(p.id || p.property_id),
      selected: p.selected,
    }));
  }

  const props = [];
  const connections = data.connections || [];

  connections.forEach((conn) => {
    (conn.properties || []).forEach((property) => {
      props.push({
        id: property.id,
        name: property.name,
        account_name: conn.account_name,
        selected: property.selected,
      });
    });
  });

  if (data.properties) {
    data.properties.forEach((property) => {
      props.push({
        id: property.id,
        name: property.name,
        selected: property.selected,
      });
    });
  }

  const seen = new Set();
  return props.filter((p) => {
    if (!p.id || seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
}

const defaultPrimaryRange = () => [
  moment().subtract(31, "days"),
  moment().subtract(1, "days"),
];

const defaultComparisonRange = (primary) => {
  const days = primary[1].diff(primary[0], "days") + 1;
  return [
    primary[0].clone().subtract(days, "days"),
    primary[0].clone().subtract(1, "days"),
  ];
};

const AnalyticsSummaryGa4Panel = ({
  visible,
  language,
  onContentLoaded,
  onContentCleared,
  ga4ContentLoaded,
}) => {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);

  const [primaryRange, setPrimaryRange] = useState(defaultPrimaryRange);
  const [useComparison, setUseComparison] = useState(true);
  const [comparisonRange, setComparisonRange] = useState(() =>
    defaultComparisonRange(defaultPrimaryRange())
  );

  const [loadGa4Loading, setLoadGa4Loading] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [productionResult, setProductionResult] = useState(null);

  useEffect(() => {
    if (!visible) return;

    let cancelled = false;
    setUsersLoading(true);

    user_managementApi()
      .then((res) => {
        if (cancelled) return;
        if (res.data.status === "success") {
          setUsers(res.data.data || []);
        } else {
          message.error("Failed to load customers");
        }
      })
      .catch(() => {
        if (cancelled) return;
        message.error("Failed to load customers");
      })
      .finally(() => {
        if (cancelled) return;
        setUsersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setSelectedUserId(null);
      setSelectedPropertyId(null);
      setProperties([]);
      setPrimaryRange(defaultPrimaryRange());
      setComparisonRange(defaultComparisonRange(defaultPrimaryRange()));
      setUseComparison(true);
      setProductionResult(null);
      return;
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || !selectedUserId) {
      setProperties([]);
      setSelectedPropertyId(null);
      return;
    }

    let cancelled = false;
    setPropertiesLoading(true);

    get_user_ga4_properties(selectedUserId)
      .then((res) => {
        if (cancelled) return;
        if (res.data.status === "success") {
          const flat = flattenGa4Properties(res.data.data);
          setProperties(flat);
          const preferred =
            flat.find((p) => p.selected)?.id || flat[0]?.id || null;
          setSelectedPropertyId(preferred);
        } else {
          setProperties([]);
          setSelectedPropertyId(null);
          message.warning(res.data.message || "No GA4 properties for this customer");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setProperties([]);
        setSelectedPropertyId(null);
        message.error(err.response?.data?.message || "Failed to load GA4 properties");
      })
      .finally(() => {
        if (cancelled) return;
        setPropertiesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, selectedUserId]);

  const formatRange = (range) => ({
    start_date: range[0].format("YYYY-MM-DD"),
    end_date: range[1].format("YYYY-MM-DD"),
  });

  const buildDatePayload = () => {
    const payload = {
      user_id: selectedUserId,
      property_id: selectedPropertyId,
      ...formatRange(primaryRange),
    };
    if (useComparison && comparisonRange?.[0] && comparisonRange?.[1]) {
      payload.comparison_start_date = comparisonRange[0].format("YYYY-MM-DD");
      payload.comparison_end_date = comparisonRange[1].format("YYYY-MM-DD");
    }
    return payload;
  };

  const handlePrimaryRangeChange = (dates) => {
    if (!dates) return;
    setPrimaryRange(dates);
    if (useComparison) {
      setComparisonRange(defaultComparisonRange(dates));
    }
  };

  const handleLoadFromGa4 = async () => {
    if (!selectedUserId || !selectedPropertyId) {
      message.warning("Select a customer and GA4 property first");
      return;
    }
    if (!primaryRange?.[0] || !primaryRange?.[1]) {
      message.warning("Select a primary date range");
      return;
    }

    try {
      setLoadGa4Loading(true);
      const payload = buildDatePayload();
      const res = await build_analytics_summary_test_content(payload);
      if (res.data.status === "success" && res.data.data?.test_content != null) {
        const content =
          typeof res.data.data.test_content === "string"
            ? res.data.data.test_content
            : JSON.stringify(res.data.data.test_content, null, 2);
        onContentLoaded?.(content);
        message.success("Test content loaded from GA4");
      } else {
        message.error(res.data.message || "Failed to build test content from GA4");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load from GA4");
    } finally {
      setLoadGa4Loading(false);
    }
  };

  const handleCompareProduction = async () => {
    if (!selectedPropertyId) {
      message.warning("Select a GA4 property first");
      return;
    }
    if (!primaryRange?.[0] || !primaryRange?.[1]) {
      message.warning("Select a primary date range");
      return;
    }

    try {
      setCompareLoading(true);
      setProductionResult(null);
      const { start_date, end_date } = formatRange(primaryRange);
      const res = await generate_web_analytics_summary({
        property_id: selectedPropertyId,
        template: "merchandize",
        start_date,
        end_date,
        language: language || "en",
      });
      if (res.data.status === "success" && res.data.data) {
        setProductionResult(res.data.data);
        message.success("Production summary loaded");
      } else {
        message.error(res.data.message || "Failed to load production summary");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to compare with production");
    } finally {
      setCompareLoading(false);
    }
  };

  const handleClearGa4 = () => {
    onContentCleared?.();
    setProductionResult(null);
  };

  const canLoad = Boolean(selectedUserId && selectedPropertyId && primaryRange?.[0]);

  return (
    <div
      style={{
        marginBottom: 16,
        padding: "12px 14px",
        background: "#f6ffed",
        border: "1px solid #b7eb8f",
        borderRadius: 8,
      }}
    >
      <Space style={{ marginBottom: 10 }} wrap>
        <Text strong style={{ fontSize: 13 }}>GA4 Data Source</Text>
        {ga4ContentLoaded && <Tag color="green">loaded from GA4</Tag>}
      </Space>

      <Row gutter={[12, 12]} align="bottom">
        <Col xs={24} sm={12} md={8}>
          <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
            Customer
          </Text>
          <Select
            showSearch
            placeholder="Select customer"
            style={{ width: "100%" }}
            loading={usersLoading}
            value={selectedUserId}
            onChange={setSelectedUserId}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.searchlabel || "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {users.map((u) => (
              <Option
                key={u._id}
                value={u._id}
                searchlabel={`${u.full_name || ""} ${u.email || ""}`}
              >
                {u.full_name || u.email}
                {u.email && u.full_name ? ` (${u.email})` : ""}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
            GA4 Property
          </Text>
          <Select
            showSearch
            placeholder={selectedUserId ? "Select property" : "Select a customer first"}
            style={{ width: "100%" }}
            loading={propertiesLoading}
            disabled={!selectedUserId}
            value={selectedPropertyId}
            onChange={setSelectedPropertyId}
            optionFilterProp="children"
          >
            {properties.map((p) => (
              <Option key={p.id} value={p.id}>
                {p.name}
                {p.account_name ? ` — ${p.account_name}` : ""}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
            Primary date range (required)
          </Text>
          <RangePicker
            style={{ width: "100%" }}
            value={primaryRange}
            onChange={handlePrimaryRangeChange}
            allowClear={false}
          />
        </Col>

        <Col xs={24} sm={24} md={10}>
          <Checkbox
            checked={useComparison}
            onChange={(e) => {
              setUseComparison(e.target.checked);
              if (e.target.checked && primaryRange) {
                setComparisonRange(defaultComparisonRange(primaryRange));
              }
            }}
            style={{ marginBottom: 4 }}
          >
            <Text type="secondary" style={{ fontSize: 11 }}>
              Include comparison period (optional)
            </Text>
          </Checkbox>
          <RangePicker
            style={{ width: "100%" }}
            value={useComparison ? comparisonRange : null}
            onChange={setComparisonRange}
            disabled={!useComparison}
            allowClear={false}
          />
        </Col>

        <Col xs={24} sm={24} md={14}>
          <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
            {ga4ContentLoaded && (
              <Button size="small" onClick={handleClearGa4}>
                Clear GA4 content
              </Button>
            )}
            <Button
              icon={<SwapOutlined />}
              loading={compareLoading}
              disabled={!canLoad}
              onClick={handleCompareProduction}
            >
              Compare with Production
            </Button>
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              loading={loadGa4Loading}
              disabled={!canLoad}
              onClick={handleLoadFromGa4}
            >
              Load from GA4
            </Button>
          </Space>
        </Col>
      </Row>

      {productionResult && (
        <Collapse style={{ marginTop: 12 }} defaultActiveKey={["production"]}>
          <Panel
            key="production"
            header={
              <Space>
                <Text strong style={{ fontSize: 12 }}>Production dashboard response</Text>
                {productionResult.summary_source && (
                  <Tag color={productionResult.summary_source === "llm" ? "green" : "cyan"}>
                    source: {productionResult.summary_source}
                  </Tag>
                )}
              </Space>
            }
          >
            <pre
              style={{
                margin: 0,
                padding: 12,
                background: "#fff",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                fontSize: 12,
                lineHeight: 1.5,
                maxHeight: 280,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(
                {
                  summary_source: productionResult.summary_source,
                  structured_insights: productionResult.structured_insights,
                },
                null,
                2
              )}
            </pre>
          </Panel>
        </Collapse>
      )}
    </div>
  );
};

export default AnalyticsSummaryGa4Panel;
